// angular imports
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowEditorSharedService } from '../../_services/workflow-editor-shared.service';
import { WorkflowSharedService } from '../../_services/workflow-shared.service';

// model imports
import { WFEBankBalanceResultDto } from '../../../workflow/_models/common/wfe-bank-balance-result-dto';
import { WFInvoiceHistoryParametersDto } from '../../_models/wf-invoice-history-parameters-dto';
import { WorkflowEFTEditorResultDto } from '../../_models/wf-vendor-eft-detail';
import { WFVendorHistoryParametersDto } from '../../_models/wf-vendor-history-parameters-dto';
import { WFEBankAccountResultDto } from '../../_models/wfe-bank-account-result-dto';
import { WFEBaseParametersDto } from '../../_models/wfe-base-parameter-dto';
import { WFEEditorParameterDto } from '../../_models/wfe-editor-parameter-dto';
import { WFESpecialHandlingResultDto } from '../../_models/wfe-special-handling-result-dto';
import { WFEWorkflowRawDto } from '../../_models/wfe-workflow-raw-dto';

// constants imports
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { WorkflowConstants } from '../../_constants/worflow-constants';

@Component({
    selector: 'app-workflow-invoice-header-editor',
    templateUrl: './workflow-invoice-header-editor.component.html',
    styleUrls: ['./workflow-invoice-header-editor.component.scss']
})
export class WorkflowInvoiceHeaderEditorComponent implements OnInit, OnChanges {
    @Input() workflowEditor: WFEWorkflowRawDto;
    @Input() vendorAccountNumber: string;
    @Input() payee: string;
    @Input() bankBalance: number;
    @Input() bankAccounts: Array<WFEBankAccountResultDto>;
    @Input() matchEntityRequiredDimensions: boolean;
    @Input() boardApprovalEnabled: boolean;
    @Input() closeAllChildPopup = false;
    @Input() closedBookPeriod: Date;
    @Input() managementCompanyId: number;
    @Input() workflowStep: string;
    @Input() allowEditInvoiceHeaderDetails: boolean;

    @Output() onBankAccountChange = new EventEmitter<WFEBankBalanceResultDto>();
    @Output() onUpdateDescription = new EventEmitter<string>();
    @Output() onEFTValueChanged = new EventEmitter<boolean>();

    payeeType: string;

    invoiceHistoryBaseParams: WFInvoiceHistoryParametersDto;
    vendorAccountHistoryParams: WFVendorHistoryParametersDto;
    ivaHistoryMode: string;
    ivaHistoryTitle: string;
    showInvoiceHistory = false;

    bankAccountSource: Array<WFEBankAccountResultDto>;
    bankAccountColumns: Array<any>;
    bankAccountBoxValue: Array<number>;

    dateFormat: string;
    currencyFormat: string;

    specialHandlingSource: Array<WFESpecialHandlingResultDto>;
    showSpecialInstructions = false;

    isLateFeeIncluded = false;
    boardApprovalRequired: boolean;

    showCodingDefaultEditor = false;

    iconNotes = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes = IconConstants.NOTES_EMPTY;
    iconEdit = IconConstants.EDIT;

    isEFTEnabled = true;

    constructor(
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService,
        private readonly workflowEditorSharedService: WorkflowEditorSharedService,
        private readonly errorService: ServerErrorService,
        private readonly workflowSharedService: WorkflowSharedService
    ) {
        this.workflowEditor = new WFEWorkflowRawDto();
    }

    ngOnInit() {
        this.dateFormat = StringFormatConstants.dateFormat;
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (Utils.notNullAndDefined(changes.bankBalance) && Utils.notNullAndDefined(changes.bankBalance.currentValue)) {
            this.bankBalance = changes.bankBalance.currentValue;
        }

        if (
            Utils.notNullAndDefined(changes.workflowEditor) &&
            Utils.notNullAndDefined(changes.workflowEditor.currentValue)
        ) {
            this.workflowEditor = changes.workflowEditor.currentValue;
            if (Utils.notNullAndDefined(this.workflowEditor.workflowId)) {
                this.payeeType = this.workflowEditor.payeeType;
                if (Utils.equalsIgnoreCase(this.payeeType, WorkflowConstants.PAYEE_TYPE_VENDOR)) {
                    this.getEFTEditorPermission();
                }
                this.isLateFeeIncluded = !!this.workflowEditor.lateFee;
                this.boardApprovalRequired = this.workflowEditor.boardApprovalRequired;
                this.populateSpecialHandling();
            }
        }

        if (Utils.notNullOrEmpty(changes.bankAccounts) && Utils.notNullOrEmpty(changes.bankAccounts.currentValue)) {
            this.bankAccounts = changes.bankAccounts.currentValue;
            this.prepareBankSource();
        }

        if (
            Utils.notNullAndDefined(changes.closeAllChildPopup) &&
            Utils.notNullAndDefined(changes.closeAllChildPopup.currentValue)
        ) {
            this.closeAllChildPopup = changes.closeAllChildPopup.currentValue;
            if (Utils.isTrue(this.closeAllChildPopup)) {
                this.closeAllPopups();
            }
        }
    }

    onInvoiceClick(): void {
        this.invoiceHistoryBaseParams = new WFInvoiceHistoryParametersDto();
        this.invoiceHistoryBaseParams.communityId = this.workflowEditor.communityId;
        this.invoiceHistoryBaseParams.invoiceNumber = this.workflowEditor.invoiceNumber;
        this.invoiceHistoryBaseParams.payeeCode = this.workflowEditor.payeeCode;
        this.ivaHistoryMode = 'invoiceHistory';
        this.ivaHistoryTitle = 'Invoice History';

        this.showInvoiceHistory = true;
    }

    onVendorAccountClick(): void {
        this.vendorAccountHistoryParams = new WFVendorHistoryParametersDto();
        this.vendorAccountHistoryParams.communityId = this.workflowEditor.communityId;
        this.vendorAccountHistoryParams.vendorAccountId = this.workflowEditor.vendorAccountId;
        this.vendorAccountHistoryParams.vendorCode = this.workflowEditor.payeeCode;
        this.ivaHistoryMode = 'vendorAccountHistory';
        this.ivaHistoryTitle = 'Vendor Account History';

        this.showInvoiceHistory = true;
    }

    onCloseHistory(): void {
        this.showInvoiceHistory = false;
    }

    onCodingDefaultClick(): void {
        this.showCodingDefaultEditor = true;
    }

    onCodingDefaultEditorClose(): void {
        this.showCodingDefaultEditor = false;
    }

    getFormattedDate(dt: Date): string {
        return Utils.notNullAndDefined(dt) ? this.helperService.getDateString(dt) : null;
    }

    getFormattedCurrency(value: number): number {
        return this.precisionService.getRoundedNumber(value);
    }

    onSpecialInstructionClick(): void {
        this.showSpecialInstructions = true;
    }

    onSpecialInstructionsPopUpClose(): void {
        this.showSpecialInstructions = false;
    }

    onBankAccountSelection(selectedObject: any): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedBankAccount = selectedObject.selectedRow[0];
            if (Utils.notEquals(selectedBankAccount.bankAccountId, this.workflowEditor.bankAccountId)) {
                this.workflowEditor.bankAccountId = selectedBankAccount.bankAccountId;
                this.onBankAccountChange.emit(selectedBankAccount);
            }
        } else {
            this.workflowEditor.bankAccountId = null;
            this.onBankAccountChange.emit(null);
        }
    }

    onUpdateDescriptionClick(): void {
        this.onUpdateDescription.emit(this.workflowEditor.description);
    }

    onEFTValueChanges(event: any): void {
        this.onEFTValueChanged.emit(event.value);
    }

    private getEFTEditorPermission(): void {
        const baseParam: WFEEditorParameterDto = new WFEEditorParameterDto();
        baseParam.communityId = this.workflowEditor.communityId;
        baseParam.workflowId = this.workflowEditor.workflowId;

        this.workflowSharedService.getEFTEditor(baseParam).subscribe(
            (result: WorkflowEFTEditorResultDto) => {
                this.isEFTEnabled = result.allowEFTEdit;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private populateSpecialHandling(clearCache = false): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowEditor.workflowId;
        baseParams.communityId = this.workflowEditor.communityId;
        baseParams.workflowStatus = this.workflowEditor.status;
        baseParams.clearCache = clearCache;

        this.workflowEditorSharedService.getWorkflowSpecialHandling(baseParams).subscribe(
            (result: WFESpecialHandlingResultDto[]) => {
                this.specialHandlingSource = result;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private prepareBankSource(): void {
        const bankAccountSource: Array<WFEBankAccountResultDto> = [];
        let selectedAccount: WFEBankAccountResultDto;
        this.bankAccounts.forEach((account) => {
            let obj: WFEBankAccountResultDto = new WFEBankAccountResultDto();
            obj = Object.assign(obj, account);

            if (Utils.equals(account.bankAccountId, this.workflowEditor.bankAccountId)) {
                selectedAccount = account;
            }

            bankAccountSource.push(obj);
        });

        this.bankAccountSource = bankAccountSource;
        if (Utils.isNullOrUndefined(selectedAccount)) {
            selectedAccount = this.bankAccounts.find((d) => Utils.isTrue(d.isAPDefault));
        }

        this.bankAccountBoxValue = Utils.notNullAndDefined(selectedAccount) ? [selectedAccount.bankAccountId] : [];

        this.prepareBankAccountColumns();
    }

    private prepareBankAccountColumns(): void {
        this.bankAccountColumns = [
            {
                dataField: 'accountName',
                dataType: 'string',
                caption: 'Bank Account'
            },
            {
                dataField: 'glAccountNumber',
                dataType: 'string',
                caption: 'GL Account Number'
            },
            {
                dataType: 'string',
                caption: 'GL Dimensions',
                cellTemplate: 'multiDimensionsTemplate'
            },
            {
                dataField: 'isAPDefault',
                dataType: 'boolean',
                caption: 'Is Default',
                cellTemplate: 'booleanValueCellTemplate'
            },
            {
                dataField: 'displayBankName',
                dataType: 'string',
                visible: false
            }
        ];
    }

    private closeAllPopups(): void {
        this.showSpecialInstructions = false;
    }

    public getWorkflowEditor(): WFEWorkflowRawDto {
        this.workflowEditor.boardApprovalRequired = this.boardApprovalRequired;
        return this.workflowEditor;
    }
}
